import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HeartPulse, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("");

  useEffect(() => {
    api.get("/public/branding").then((r) => setLogo(r.data?.logo || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      nav(location.state?.from || "/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Accesso non riuscito");
    } finally { setLoading(false); }
  };

  const demo = (em) => { setEmail(em); setPassword("Habitat2025!"); };


  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "var(--gr-sidebar)" }}>
        <div className="flex items-center gap-3">
          {logo
            ? <img src={logo} alt="Logo azienda" data-testid="login-logo" className="h-11 max-w-[160px] object-contain bg-white rounded-xl p-1" />
            : <div className="h-11 w-11 rounded-xl grid place-items-center font-bold" style={{ background: "var(--gr-primary)" }}>GR</div>}
          <span className="font-head text-xl font-bold">Gestione Residenze</span>
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold leading-tight mb-4">La gestione completa<br />delle tue strutture socio-assistenziali</h1>
          <p className="text-[#b9cdc4] text-lg">Ospiti, operatori, consegne, terapie, turni e controllo di gestione — tutto in un'unica piattaforma sicura e multi-struttura.</p>
        </div>
        <p className="text-[#7f9a8f] text-sm">© 2026 Gestione Residenze — Piattaforma SaaS</p>
      </div>

      <div className="flex items-center justify-center p-8 bg-[var(--gr-bg)]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            {logo
              ? <img src={logo} alt="Logo azienda" data-testid="login-logo-mobile" className="h-9 max-w-[140px] object-contain" />
              : <HeartPulse className="text-primary" />} <span className="font-head text-xl font-bold">Gestione Residenze</span>
          </div>
          <h2 className="text-2xl font-head font-bold mb-1">Accedi</h2>
          <p className="text-muted-foreground text-sm mb-6">Inserisci le tue credenziali per continuare</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="mb-1 block">Email</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "email"} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="pr-10" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Password</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="pr-10" />
                <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="login-toggle-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  title={showPw ? "Nascondi password" : "Mostra password"} aria-label={showPw ? "Nascondi password" : "Mostra password"}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tocca l'occhiolino per mostrare o nascondere quello che scrivi</p>
            </div>
            <Button type="submit" className="w-full font-semibold" disabled={loading} data-testid="login-submit">
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
